import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertCircle, ChevronDown, MapPin } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import BeforeAfterJobFlow from "@/components/od/BeforeAfterJobFlow";
import { CLUSTER_COPY, CLUSTER_COPY_EXTRA, CLUSTER_EXTRAS } from "@/data/clusterCopy";

const SITE = "https://oceandata.ca";
const ALL_CLUSTERS = { ...CLUSTER_COPY, ...CLUSTER_COPY_EXTRA };
// Priority-1 pages get the before/after job-flow visual.
const BEFORE_AFTER_SLUGS = new Set(["zoho-fsm", "zoho-fsm-accounting"]);

const CLUSTER_NAMES = {
  "zoho-fsm": { en: "Zoho FSM for field service", fr: "Zoho FSM pour le service sur le terrain", es: "Zoho FSM para servicio de campo" },
  "zoho-books-vs-quickbooks": { en: "Zoho Books or QuickBooks for contractors", fr: "Zoho Books ou QuickBooks pour entrepreneurs", es: "Zoho Books o QuickBooks para contratistas" },
  "zoho-iot-connected-buildings": { en: "Connected buildings with Zoho IoT", fr: "Bâtiments connectés avec Zoho IoT", es: "Edificios conectados con Zoho IoT" },
  "zuper-field-service": { en: "Zuper evaluation & consulting", fr: "Évaluation et conseil Zuper", es: "Evaluación y consultoría Zuper" },
  "zoho-fsm-vs-zuper": { en: "Zoho FSM vs Zuper", fr: "Zoho FSM ou Zuper", es: "Zoho FSM vs Zuper" },
  "zoho-projects-construction": { en: "Zoho Projects for construction", fr: "Zoho Projects pour la construction", es: "Zoho Projects para construcción" },
  "zoho-fsm-accounting": { en: "Zoho FSM accounting integrations", fr: "Intégrations comptables Zoho FSM", es: "Integraciones contables de Zoho FSM" },
};

const CITY_LINKS = {
  en: [["Phoenix", "/phoenix"], ["Scottsdale", "/scottsdale"], ["Fort Lauderdale", "/fort-lauderdale"], ["Miami", "/miami"], ["Montréal", "/montreal"]],
  fr: [["Phoenix", "/fr/phoenix"], ["Scottsdale", "/fr/scottsdale"], ["Fort Lauderdale", "/fr/fort-lauderdale"], ["Miami", "/fr/miami"], ["Montréal", "/fr/montreal"]],
  es: [["Phoenix", "/es/phoenix"], ["Scottsdale", "/es/scottsdale"], ["Fort Lauderdale", "/es/fort-lauderdale"], ["Miami", "/es/miami"], ["Montreal", "/es/montreal"]],
};

const RELATED_LABEL = { en: "Keep exploring", fr: "À explorer aussi", es: "Siga explorando" };

export default function ClusterPage({ slug, lang }) {
  const t = ALL_CLUSTERS[slug]?.[lang];
  const extras = CLUSTER_EXTRAS[slug]?.[lang];
  const { setOpen } = useSimplify();
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    if (t) document.title = t.docTitle;
  }, [t]);

  if (!t) return null;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t.schemaName,
      inLanguage: lang === "fr" ? "fr-CA" : lang === "es" ? "es" : "en-CA",
      provider: { "@type": "Organization", name: "Ocean Data", url: SITE },
      description: t.sub,
      url: `${SITE}${t.path}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ocean Data", item: SITE },
        { "@type": "ListItem", position: 2, name: t.schemaName, item: `${SITE}${t.path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: lang === "fr" ? "fr-CA" : lang === "es" ? "es" : "en-CA",
      mainEntity: t.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const relatedClusters = Object.keys(ALL_CLUSTERS)
    .filter((s) => s !== slug && ALL_CLUSTERS[s][lang])
    .map((s) => ({ label: CLUSTER_NAMES[s][lang], href: ALL_CLUSTERS[s][lang].path }));

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" data-testid={`cluster-${slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">{t.eyebrow}</p>
          <h1 className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            <MaskedLine delay={0.15} testId={`cluster-${slug}-h1`}>{t.h1}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9"
          >
            <button
              data-testid={`cluster-${slug}-cta`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              {t.cta} <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pains */}
      <section className="py-14 lg:py-20 bg-white border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">{t.painsTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.pains.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div data-testid={`cluster-${slug}-pain-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 px-6 py-5 flex items-start gap-3.5">
                  <AlertCircle size={17} className="text-brand-gold mt-0.5 shrink-0" />
                  <p className="text-base text-slate-700 leading-relaxed">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Before/after job-flow visual (priority pages) */}
      {BEFORE_AFTER_SLUGS.has(slug) && (
        <section className="py-4 lg:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
            <BeforeAfterJobFlow lang={lang} />
          </div>
        </section>
      )}

      {/* Workflow chain — which tool carries each stage */}
      {extras?.workflow && (
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
            <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">{extras.workflowTitle}</h2>
            <Reveal>
              <div data-testid={`cluster-${slug}-workflow`} className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {extras.workflow.map((w, i) => (
                    <div key={w.stage} className="flex items-center gap-2 min-w-0">
                      <div data-testid={`cluster-${slug}-stage-${i}`} className="flex-1 min-w-0 h-full rounded-xl bg-brand-tealsoft border border-brand-tealborder px-4 py-3 [overflow-wrap:anywhere]">
                        <p className="font-display font-semibold text-brand-navy text-sm">{i + 1}. {w.stage}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-brand-teal mt-1">{w.tool}</p>
                      </div>
                      {i < extras.workflow.length - 1 && <ArrowRight size={15} className="text-brand-teal shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            {extras.regionsNote && (
              <Reveal delay={0.08}>
                <p data-testid={`cluster-${slug}-regions`} className="mt-6 inline-flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <MapPin size={15} className="text-brand-teal mt-0.5 shrink-0" />
                  {extras.regionsNote}
                </p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Approach — native / configuration / integration / custom */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">{t.approachTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.approach.map((a, i) => (
              <Reveal key={a.tag} delay={i * 0.05}>
                <div data-testid={`cluster-${slug}-approach-${i}`} className="h-full rounded-2xl bg-white border border-slate-200 px-6 py-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-3">{a.tag}</p>
                  <p className="text-base text-slate-700 leading-relaxed">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div data-testid={`cluster-${slug}-honest-note`} className="mt-8 rounded-2xl bg-brand-tealsoft border border-brand-tealborder px-7 py-6 flex items-start gap-3.5">
              <CheckCircle2 size={18} className="text-brand-teal mt-0.5 shrink-0" />
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{t.honestNote}</p>
            </div>
          </Reveal>
          {extras?.extraNote && (
            <Reveal delay={0.14}>
              <div data-testid={`cluster-${slug}-extra-note`} className="mt-4 rounded-2xl border border-slate-200 bg-white px-7 py-6 flex items-start gap-3.5">
                <AlertCircle size={18} className="text-brand-gold mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{extras.extraNote}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">{t.faqTitle}</h2>
          <div className="space-y-3">
            {t.faqs.map((f, i) => (
              <div key={f.q} className="rounded-2xl border border-slate-200 bg-[#F8FAFC] overflow-hidden">
                <button
                  data-testid={`cluster-${slug}-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display font-semibold text-base text-brand-navy">{f.q}</span>
                  <ChevronDown size={17} className={`text-brand-teal shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <p className="px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                )}
              </div>
            ))}
          </div>

          {/* Internal links — related guides + local pages */}
          <div className="mt-12 pt-8 border-t border-slate-100">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-4">{RELATED_LABEL[lang]}</p>
            <div className="flex flex-wrap gap-2.5">
              {relatedClusters.map((r) => (
                <Link key={r.href} to={r.href} className="rounded-full border border-slate-200 bg-white text-brand-navy text-xs font-medium px-4 py-2 transition-colors duration-300 hover:border-brand-teal hover:text-brand-teal">
                  {r.label}
                </Link>
              ))}
              {CITY_LINKS[lang].map(([label, href]) => (
                <Link key={href} to={href} className="rounded-full bg-brand-tealsoft border border-brand-tealborder text-brand-navy text-xs font-medium px-4 py-2 transition-colors duration-300 hover:bg-brand-teal hover:text-white">
                  {label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <button
              data-testid={`cluster-${slug}-bottom-cta`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.cta} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
